import { useCallback, useEffect, useMemo, useRef } from "react"

import { UserActivityEventType } from "../generated/graphql"
import { captureActivity, startActivityDelivery } from "./activity"
import { trackAmplitudeEvent } from "./amplitude"
import { landingEntry } from "./landing"
import { useCommunityCustomization } from "./useCommunityCustomization"

// Every page view carries where the visit came from (utils/landing.ts), so sources can be read
// per event and per community: the community page (InviteLandingOpened), event and ticket pages,
// a shop product page (ShopModuleOpened), tickets on screen in the shop (TicketOptionsViewed) and
// the news and radio previews shared links open (NewsItemOpened, RadioTrackOpened).
const LANDING_TYPES = new Set<UserActivityEventType>([
  UserActivityEventType.InviteLandingOpened,
  UserActivityEventType.EventScreenOpened,
  UserActivityEventType.EventTicketsScreenOpened,
  UserActivityEventType.ShopModuleOpened,
  UserActivityEventType.TicketOptionsViewed,
  UserActivityEventType.NewsItemOpened,
  UserActivityEventType.RadioTrackOpened,
])

interface TrackEvent {
  type: UserActivityEventType
  communityCode?: string
  data?: Record<string, unknown>
}

export const useTrackUserActivity = () => {
  const customizationData = useCommunityCustomization()

  const communityCode = customizationData?.code
  const communityName = customizationData?.name

  useEffect(() => {
    startActivityDelivery()
  }, [])

  const track = useCallback(
    ({ type, communityCode: overrideCode, data }: TrackEvent) => {
      const cc = overrideCode ?? communityCode
      // One occurrence, two deliveries: the shared ID lines Amplitude up with WERZ rows.
      const occurrenceId = crypto.randomUUID()
      const _data = {
        ...data,
        ...(LANDING_TYPES.has(type) ? { entry: landingEntry() } : {}),
        platform: "web-members",
      }
      const eventProperties = {
        communityCode: cc,
        communityName,
        ..._data,
        loggedIn:
          typeof window !== "undefined" ? !!window.localStorage.getItem("accessToken") : false,
        occurrenceId,
        platform: "Web",
      }
      void trackAmplitudeEvent(type, eventProperties)

      // Every environment delivers; the API decides what it accepts. Queued synchronously, so a
      // full page load right after capture cannot lose the event.
      captureActivity(
        { communityCode: cc, data: _data, type },
        { capturedAt: new Date().toISOString(), occurrenceId },
      )
      // Development only: in production Sentry would keep it as a breadcrumb, data included.
      if (process.env.NODE_ENV === "development") console.log("track", type, _data)
    },
    [communityCode, communityName],
  )

  return track
}

// One page visit: a new ID per route key, stable across re-renders such as auth hydration.
export const useVisitId = (routeKey: string | undefined) =>
  // biome-ignore lint/correctness/useExhaustiveDependencies: a changed route key is a new visit
  useMemo(() => crypto.randomUUID(), [routeKey])

// Tracks `event`, with `visitId` added to its data, once per visit, as soon as the caller can build
// it (usually once its data has loaded). Runs after every render on purpose: the visit guard makes
// that free, and `track` must not be an effect dependency.
export const useTrackOncePerVisit = (visitId: string, event: TrackEvent | undefined) => {
  const track = useTrackUserActivity()
  const trackedVisit = useRef<string>()
  useEffect(() => {
    if (!event || trackedVisit.current === visitId) return
    trackedVisit.current = visitId
    track({ ...event, data: { ...event.data, visitId } })
  })
}
